<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useClassStore, useQuizStore, useSignalRStore } from '../../../../stores';
import { QuestionState, QuizState } from '../../../../types/quiz';
import { AssessmentQuestion, CountdownType } from '../../../../types/assessment';
import { TEXT } from '../../../../configs/text';
import { useI18n } from 'petite-vue-i18n';
import { Vue3Lottie } from 'vue3-lottie'

import ButtonV2 from '../../../General/ButtonV2.vue';
import Countdown from '../../../Assessment/Countdown.vue'
import LeaderBoard from './components/LeaderBoard.vue';
import Dialogue from '../../../Assessment/Dialogue.vue';
import clockSpining from '../../../../assets/animations/clock_spining.json'
import Slide from '../../../Animation/Slide.vue';
import Fade from '../../../Animation/Fade.vue';
import QuizBGMVolumeControl from './components/QuizBGMVolumeControl.vue';
import QuizReview from './QuizReview.vue';
import QuizPlay from './QuizPlay.vue';





const { t } = useI18n()
const signalr = useSignalRStore()
const quizStore = useQuizStore();
const classStore = useClassStore()

const studentSubmissions = ref<number>(0);
const dialogueRef = ref();

const curentQuiz = computed<AssessmentQuestion | undefined>(() => {
    return quizStore.quiz.questions.find((question: AssessmentQuestion) => question.id == quizStore.questionState?.id)
})
const nextQuestion = computed<AssessmentQuestion | undefined>(() => quizStore.quiz?.questions[quizStore.curentQuestionIndex + 1]);

const isNextButtonDisabled = computed(() => {
    if (quizStore.curentQuestionIndex < quizStore.quiz.questions.length - 1) return false;
    return quizStore.quizSetting.showCorrectAnswer === 1 ? quizStore.questionState?.state === QuestionState.REVIEW : quizStore.questionState?.state === QuestionState.FINISHED


})
watch(() => classStore.selectedClassDetails?.students, (student) => {
    studentSubmissions.value = student?.filter(student => {
        return student.quizQuestion?.some(answer => answer.id === curentQuiz.value?.id)
    }).length || 0
})

watch(() => studentSubmissions.value, (submitssion) => {
    if (submitssion >= classStore.attendedStudentCount) {
        handleFinishQuestion();
    }
})

async function nextButtonClickHandler() {
    switch (quizStore.questionState?.state) {
        case QuestionState.ON_GOING:
            try {
                await dialogueRef.value?.showModal({
                    title: t(TEXT.change_question),
                    text: t(TEXT.change_question_text, { 'student': classStore.attendedStudentCount - studentSubmissions.value }),
                    confirmButtonText: t(TEXT.confirm_popup_btn),
                    cancelButtonText: t(TEXT.cancel_popup_btn),
                    reverse: true
                });
                handleFinishQuestion();
            } catch {
                return
            }
            break;
        case QuestionState.FINISHED:
            if (quizStore.quizSetting.showCorrectAnswer === 1) {
                signalr.reviewCurrentQuestion();
            } else {
                if (!nextQuestion.value) {
                    handleStopQuiz();
                } else {
                    handleChangeQuestion(nextQuestion.value.id, nextQuestion.value.questionTime);
                }
            }
            break;
        case QuestionState.REVIEW:

            if (!nextQuestion.value) {
                handleStopQuiz();
            } else {
                handleChangeQuestion(nextQuestion.value.id, nextQuestion.value.questionTime);
            }
            break;
        default:
            break;
    }
}

async function handleStopQuiz() {
    if (quizStore.quizState != QuizState.ON_GOING) return;
    try {
        await dialogueRef.value?.showModal({
            title: t(TEXT.finish_quiz),
            text: t(TEXT.finish_quiz_text),
            confirmButtonText: t(TEXT.confirm_popup_btn),
            cancelButtonText: t(TEXT.cancel_popup_btn),
            reverse: true
        });
        signalr.quizFinished()
    } catch {
        return
    }
}

function handleFinishQuestion() {
    if (quizStore.quizState != QuizState.ON_GOING) return;
    signalr.finishedCurrentQuestion()
}

async function handleChangeQuestion(id: number, time: number) {
    signalr.quizChangeQuestion(id, time);
}


</script>

<template>
    <div class="relative flex flex-col justify-end h-full w-full overflow-hidden" v-if="quizStore.quiz && curentQuiz?.id && quizStore.questionState">
        <Fade>
            <div class="flex items-start h-12 py-3 px-10 gap-3" v-if="quizStore.questionState.state === QuestionState.ON_GOING">
                <div class="relative w-full mt-3 rounded-full">
                    <Vue3Lottie class="absolute -left-4 -top-3" ref="lottieContainer" :animationData="clockSpining" :width="40" :height="40" />
                    <Countdown :type="CountdownType.BAR" :duration="curentQuiz.questionTime" :current="quizStore.timeRemaining" @finished="handleFinishQuestion" :key="curentQuiz.id">
                    </Countdown>
                </div>
            </div>
            <div class="w-full bg-cyan-500 dark:bg-purple-800 text-center font-bold text-lg text-white p-2 rounded-b-2xl" v-if="quizStore.questionState?.state === QuestionState.REVIEW">
                {{ t(TEXT.correct_answer) }}
            </div>
        </Fade>
        <Fade>
            <div class="flex flex-col w-full h-full gap-5 overflow-hidden" v-if="quizStore.questionState.state === QuestionState.ON_GOING">
                <QuizPlay :question="curentQuiz" disabled/>
            </div>
            <div class="flex flex-col w-full h-full gap-5 overflow-hidden" v-if="quizStore.questionState.state === QuestionState.REVIEW">
                <QuizReview :question="curentQuiz" />
            </div>

            <template v-if="quizStore.questionState.state === QuestionState.FINISHED">
                <Slide direction="down">
                    <div class="w-full h-full flex flex-col gap-5 justify-center items-center" v-if="quizStore.showStudentPoint">
                        <div class="text-4xl text-orange-500 dark:text-white font-bold first-letter:capitalize">{{
                            t(TEXT.count_finish_question) }}</div>
                        <Countdown :type="CountdownType.CIRCULAR" :duration="3" @finished="quizStore.showStudentPoint = false">
                        </Countdown>
                    </div>

                    <div class="p-10 h-full overflow-auto" v-if="!quizStore.showStudentPoint">
                        <LeaderBoard />
                    </div>
                </Slide>
            </template>
        </Fade>

        <Slide direction="down">
            <div class="flex gap-3 sm:flex-row justify-between items-center w-full bg-white px-5 py-1 rounded-lg">
                <div class="flex items-center gap-3">
                    <QuizBGMVolumeControl class="text-black dark:text-zinc-700 size-10" />

                    <div class="hidden sm:flex md:flex-col gap-1 items-center font-bold">
                        <p class="text-orange-500 dark:text-rose-600 capitalize">{{ t(TEXT.question) }}</p>
                        <p class="text-black dark:text-zinc-700">{{ `${quizStore.curentQuestionIndex +
                            1}/${quizStore.quiz?.questions.length}`
                        }}
                        </p>
                    </div>
                </div>
                <div class="hidden sm:flex md:flex-col gap-1 items-center font-bold">
                    <p class="text-orange-500 dark:text-rose-600">{{ t(TEXT.submission) }}</p>
                    <p class="text-black dark:text-zinc-700">{{ `${studentSubmissions} /
                        ${classStore.attendedStudentCount}` }}
                    </p>
                </div>
                <div class="grid grid-cols-2 items-center justify-center gap-3">
                    <ButtonV2 :color="quizStore.darkMode ? 'danger' : 'primary'" class="w-full" animation @click="nextButtonClickHandler" :disabled="isNextButtonDisabled">{{ t(TEXT.next_btn) }}
                    </ButtonV2>
                    <ButtonV2 :color="quizStore.darkMode ? 'danger' : 'info'" class="w-full" animation @click="handleStopQuiz">
                        {{ t(TEXT.end_quiz_btn) }}
                    </ButtonV2>
                </div>
            </div>
        </Slide>
        <Dialogue ref="dialogueRef" :darkMode="quizStore.darkMode" />
    </div>
</template>
<style scoped>
.slide-enter-active,
.slide-leave-active,
.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-enter-active,
.slide-left-leave-active,
.slide-reverse-enter-active,
.slide-reverse-leave-active {
    transition: all 0.25s ease-in-out;
}

.slide-enter-from,
.slide-left-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-leave-to,
.slide-right-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-reverse-enter-from,
.slide-right-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-reverse-leave-to,
.slide-left-leave-to {
    opacity: 0;
    transform: translateX(30px);
}
</style>